import { TIMING_MS } from '@/constants/timing';
import type React from 'react';
import { useEffect } from 'react';

type UseAutoResizeColumnsParams = {
  apiRef: React.MutableRefObject<any>;
  columns: any[];
  enabled?: boolean;
  /**
   * Extra deps to watch (e.g., data, grouping models, filters, etc.)
   */
  deps?: any[];
  /**
   * Preferred column field to auto-size.
   * If not found, will fall back to the first column.
   */
  preferredField?: string;
  /**
   * Delay in ms before running autosize (allows grid to finish rendering).
   */
  delay?: number;
};

export function useAutoResizeColumns({
  apiRef,
  columns,
  enabled = true,
  deps = [],
  preferredField,
  delay = TIMING_MS.GRID_AUTOSIZE_DELAY,
}: UseAutoResizeColumnsParams) {
  useEffect(() => {
    if (!enabled || !apiRef.current || !columns.length) {
      return;
    }

    const timeoutId = setTimeout(() => {
      const currentApiRef = apiRef.current;
      if (!currentApiRef) return;

      try {
        // If the grid has already unmounted, autosizeColumns can throw async (promise rejection).
        // Guarding here prevents "Cannot read properties of null" errors from MUI internals.
        const rootEl = currentApiRef.rootElementRef?.current as
          | HTMLElement
          | null
          | undefined;
        if (!rootEl || !rootEl.isConnected) {
          return;
        }

        const allColumns = currentApiRef.getAllColumns?.() ?? [];
        if (!allColumns.length) return;

        const targetColumn =
          (preferredField
            ? allColumns.find((col: any) => col.field === preferredField)
            : null) || allColumns[0];

        if (!targetColumn) return;

        const result = currentApiRef.autosizeColumns?.({
          columns: [targetColumn.field],
          includeHeaders: true,
        });

        // autosizeColumns is async; make sure rejections don't become "Uncaught (in promise)"
        // when the grid is mid-unmount or the header isn't in the DOM yet.
        if (
          result &&
          typeof (result as Promise<unknown>).catch === 'function'
        ) {
          (result as Promise<unknown>).catch(() => {
            // ignore grid readiness/unmount races
          });
        }
      } catch {
        // silently ignore grid readiness issues
      }
    }, delay);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRef, columns, delay, preferredField, enabled, ...deps]);
}
